import PersonalApiKeysManagedUsersCallout from "components/ApiKeys/PersonalApiKeys/ManagedUsersCallout";
import usePersonalApiKeysTableSort from "components/ApiKeys/PersonalApiKeys/Table/useSort";
import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import ListContentWrapper from "components/ListContentWrapper";
import PageLoading from "components/loading/PageLoading";
import PageInfoHeader from "ds/components/PageInfoHeader";
import TableSimple from "ds/components/TableSimple";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";

import { SettingsContext } from "../../Context";
import { UserManagementActivationStatus } from "../../types";
import { FILTERS_ORDER_SETTINGS_KEY as USERS_FILTERS_ORDER_SETTINGS_KEY } from "../../Users/ListNew/constants";
import { UserViewDetails } from "../types";
import UserPersonalApiKeysEmpty from "./Empty";
import { USER_PERSONAL_API_KEYS_TABLE_CELLS } from "./Table/Cells";
import {
  USER_PERSONAL_API_KEYS_FILTERS_ORDER_KEY,
  USER_PERSONAL_API_KEYS_INITIAL_SORT,
  USER_PERSONAL_API_KEYS_SORT_CONFIG,
  USER_PERSONAL_API_KEYS_SORT_FIELDS,
} from "./Table/constants";
import useUserPersonalApiKeysTableStore from "./Table/useTableStore";
import useUserPersonalApiKeysData from "./useData";

type UserPersonalApiKeysProps = {
  user: UserViewDetails;
};

const UserPersonalApiKeys = ({ user }: UserPersonalApiKeysProps) => {
  const { activationStatus } = useTypedContext(SettingsContext);
  const { apiKeys, isInitialLoading, error } = useUserPersonalApiKeysData(user.id, user.username);
  const sortedApiKeys = usePersonalApiKeysTableSort(
    apiKeys,
    USER_PERSONAL_API_KEYS_SORT_FIELDS,
    USER_PERSONAL_API_KEYS_INITIAL_SORT
  );

  useTitle(`Organization Settings · Users · ${user.username} · Personal API keys`);

  useBreadcrumbs(
    [
      {
        title: "Organization Settings",
      },
      {
        title: "Users",
        link: getBreadcrumbsBackUrl("/settings/users", USERS_FILTERS_ORDER_SETTINGS_KEY),
      },
      {
        title: user.username,
      },
    ],
    []
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (isInitialLoading) {
    return <PageLoading />;
  }

  const hasApiKeys = apiKeys.length > 0;
  const isManagedUsersActive = activationStatus === UserManagementActivationStatus.ACTIVE;

  return (
    <>
      {!isManagedUsersActive && (
        <PersonalApiKeysManagedUsersCallout
          analyticsPage={AnalyticsPageOrganization.OrganizationUserPersonalApiKeys}
        />
      )}
      <PageInfoHeader title="Personal API keys" />
      <ListContentWrapper>
        {hasApiKeys && (
          <TableSimple
            data={sortedApiKeys}
            store={useUserPersonalApiKeysTableStore}
            ariaLabel="User personal API keys table"
            cellPerColumn={USER_PERSONAL_API_KEYS_TABLE_CELLS}
            sortConfig={USER_PERSONAL_API_KEYS_SORT_CONFIG}
            filtersStorageKey={USER_PERSONAL_API_KEYS_FILTERS_ORDER_KEY}
            hideSettings
            hasPagination={false}
          />
        )}
        {!hasApiKeys && <UserPersonalApiKeysEmpty />}
      </ListContentWrapper>
    </>
  );
};

export default UserPersonalApiKeys;
